import React, { useState, useEffect, useMemo } from "react";
import { FaPlus, FaEdit, FaTrash, FaFileExport } from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import {
  getData,
  postData,
  putData,
  deleteData,
} from "../../../services/FetchNodeservices";

const Vendor = () => {
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    location: "",
    search: "",
  });
  const [selected, setSelected] = useState([]);

  const [form, setForm] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    const res = await getData("vendor/all");
    if (res?.status) setVendors(res.data);
  };

  const resetFilters = () =>
    setFilters({ status: "", location: "", search: "" });

  /* ================= FILTER ================= */
  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      const matchesStatus =
        !filters.status || v.status === filters.status;
      const matchesLocation =
        !filters.location || v.location === filters.location;
      const matchesSearch =
        !filters.search ||
        Object.values(v)
          .join(" ")
          .toLowerCase()
          .includes(filters.search.toLowerCase());
      return matchesStatus && matchesLocation && matchesSearch;
    });
  }, [vendors, filters]);

  const uniqueLocations = [
    ...new Set(vendors.map((v) => v.location).filter(Boolean)),
  ];

  /* ================= MODALS ================= */
const openAddModal = () => {
  setEditIndex(null);
  setForm({
    company_name: "",
    company_website: "",
    describe_company: "",
    vertical: "",
    contact_person: "",
    mobile_number: "",
    email_id: "",
    personal_email: "",
    designation: "",
    location: "",
    address: "",
    gst_number: "",
    linkedin_profile: "",
    remark: "",
    status: "Active",
    agreement_from: "",
    agreement_to: "",
     percentage: "",
  });
  setModalOpen(true);
};

  const openEditModal = (index) => {
    setEditIndex(index);
    setForm({ ...filteredVendors[index] });
    setModalOpen(true);
  };

  const saveVendor = async () => {
    let result;
    if (editIndex !== null) {
      result = await putData(
        `vendor/update/${filteredVendors[editIndex].vendor_id}`,
        form
      );
    } else {
      result = await postData("vendor/add", form);
    }
    if (result?.status) fetchVendors();
    setModalOpen(false);
  };

  const deleteVendor = async (id) => {
    const res = await deleteData(`vendor/delete/${id}`);
    if (res?.status) fetchVendors();
  };

  /* ================= CHECKBOX ================= */
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const selectAll = (checked) => {
    setSelected(
      checked ? filteredVendors.map((x) => x.vendor_id) : []
    );
  };

  /* ================= EXPORT ================= */
  const exportData = () => {
    const exportItems = selected.length
      ? vendors.filter((v) => selected.includes(v.vendor_id))
      : filteredVendors;

    if (!exportItems.length) return alert("No records to export");

    const ws = XLSX.utils.json_to_sheet(exportItems);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors");
    XLSX.writeFile(wb, "vendors.xlsx");
  };

  /* ================= UI ================= */
  const isDashboardView = window.location.pathname.includes("/dashboard");

  return (
    <div className={isDashboardView ? "min-h-screen bg-slate-50 p-2" : "min-h-screen bg-slate-50 pt-20 px-4 md:ml-72 md:px-6"}>

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-5">
        <h2 className="text-lg md:text-xl font-bold text-slate-800">
          Vendors @ Morgan
        </h2>

        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-xs font-semibold rounded-xl shadow-sm hover:bg-indigo-700 transition-all duration-200 active:scale-[0.98] w-fit"
        >
          <FaPlus /> Add Vendor
        </button>
      </div>

      {/* FILTERS (FIXED SPACING) */}
      <div
        className="
          bg-white rounded-2xl shadow-sm border border-slate-200/80 mb-4
          p-4
          flex flex-col sm:flex-row flex-wrap
          gap-4
          items-end
        "
      >
        <div className="w-full sm:w-48">
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Search</label>
          <input
            className="border border-slate-200 rounded-xl px-3 py-1.5 w-full text-slate-700 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50/30 transition-colors"
            placeholder="Search..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value })
            }
          />
        </div>

        <div className="w-full sm:w-40">
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Status</label>
          <select
            className="border border-slate-200 rounded-xl px-3 py-1.5 w-full text-slate-700 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50/30 transition-colors"
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value })
            }
          >
            <option value="">All</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>

        <div className="w-full sm:w-40">
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Location</label>
          <select
            className="border border-slate-200 rounded-xl px-3 py-1.5 w-full text-slate-700 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50/30 transition-colors"
            value={filters.location}
            onChange={(e) =>
              setFilters({ ...filters, location: e.target.value })
            }
          >
            <option value="">All</option>
            {uniqueLocations.map((loc) => (
              <option key={loc}>{loc}</option>
            ))}
          </select>
        </div>

        <button
          onClick={resetFilters}
          className="px-4 py-1.5 border border-slate-200 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-semibold transition-colors"
        >
          Reset
        </button>

        <button
          onClick={exportData}
          className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-800 text-white rounded-xl text-sm font-semibold hover:bg-slate-900 transition-all duration-200 active:scale-[0.98] w-fit"
        >
          <FaFileExport /> Export
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-white shadow-sm rounded-2xl border border-slate-200/80 overflow-x-auto">
        <table className="min-w-[1400px] w-full text-sm">
          <thead>
            <tr className="bg-slate-50/75 border-b border-slate-250 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
              <th className="px-6 py-3.5 text-center w-16">
                <input
                  type="checkbox"
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300"
                  checked={
                    selected.length > 0 &&
                    selected.length === filteredVendors.length
                  }
                  onChange={(e) => selectAll(e.target.checked)}
                />
              </th>
              {[
                "ID",
                "Company",
                "Website",
                "Description",
                "Contact",
                "Mobile",
                "Email",
                "Location",
                "Percentage",
                "Status",
                "Actions",
              ].map((h) => (
                <th key={h} className="px-6 py-3.5 text-left whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {filteredVendors.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-6 py-6 text-center text-slate-500">
                  No records found
                </td>
              </tr>
            ) : (
              filteredVendors.map((v, index) => (
                <tr key={v.vendor_id} className="hover:bg-slate-50/50 border-b border-slate-100 transition-colors duration-150">
                  <td className="px-6 py-4 text-center">
                    <input
                      type="checkbox"
                      className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 border-slate-300"
                      checked={selected.includes(v.vendor_id)}
                      onChange={() => toggleSelect(v.vendor_id)}
                    />
                  </td>
                  <td className="px-6 py-4 text-slate-500 whitespace-nowrap">{v.vendor_id}</td>
                  <td className="px-6 py-4 font-semibold text-slate-800 whitespace-nowrap">
                    {v.company_name}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {v.company_website ? (
                      <a
                        href={v.company_website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                      >
                        Visit
                      </a>
                    ) : "-"}
                  </td>

                  <td className="px-6 py-4 text-slate-600 max-w-xs truncate">
                    {v.describe_company || "-"}
                  </td>

                  <td className="px-6 py-4 text-slate-700 font-medium whitespace-nowrap">{v.contact_person}</td>
                  <td className="px-6 py-4 text-slate-600 whitespace-nowrap">{v.mobile_number}</td>
                  <td className="px-6 py-4 text-slate-600 whitespace-nowrap">{v.email_id}</td>
                  <td className="px-6 py-4 text-slate-600 whitespace-nowrap">{v.location}</td>
                  <td className="px-6 py-4 text-slate-750 font-semibold whitespace-nowrap">
                    {v.percentage ? `${v.percentage}%` : "-"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold
                        ${
                          v.status === "Active"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200/50"
                            : "bg-rose-50 text-rose-700 border border-rose-200/50"
                        }`}
                    >
                      {v.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(index)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
                        title="Edit Vendor"
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => deleteVendor(v.vendor_id)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-200"
                        title="Delete Vendor"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl p-6 rounded-2xl shadow-xl border border-slate-100">
            <h3 className="font-bold text-lg text-slate-800 mb-4">
              {editIndex !== null ? "Edit Vendor" : "Add Vendor"}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-h-[60vh] overflow-y-auto pr-2">
              {Object.keys(form).map((key) => {
                if (["vendor_id", "created_at"].includes(key)) return null;
                return (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                      {key.replace(/_/g, " ")}
                    </label>
                    <input
                      value={form[key] || ""}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.value })
                      }
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2.5 mt-6">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveVendor}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors shadow-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendor;
